import { zodResolver } from '@hookform/resolvers/zod';
import { resetPasswordSchema } from '@willnq/shared/validation/authSchemas';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { z } from 'zod';

import { ROUTES } from '@/constants/routes';
import { authService } from '@/services/authService';
import { parseRestError } from '@/utils/errorUtils';

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

/** Drives `ResetPasswordForm`. `email`/`token` come from the emailed reset link's query string
 * (`?email=...&token=...`, built by `PasswordResetEmailHandler`) — prefilled and never retyped by the
 * user, and therefore never rendered, which is why an incomplete link needs its own visible state. */
export function useResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);

  const email = searchParams.get('email') ?? '';
  const token = searchParams.get('token') ?? '';

  /**
   * Neither field is rendered, so neither can show a validation error. Without this the form fails
   * `handleSubmit` silently on an incomplete link: no request, no message, a button that does
   * nothing when pressed. The screen has to say the link is the problem, because the person looking
   * at it has done nothing wrong.
   */
  const linkIsIncomplete = email === '' || token === '';

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      email,
      token,
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await authService.resetPassword(values);
      navigate(ROUTES.login, { replace: true });
    } catch (error) {
      const { message } = parseRestError(error);
      setServerError(message);
    }
  });

  return { form, onSubmit, serverError, linkIsIncomplete };
}
